import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';

interface State {
  readonly error: Error | null;
}

interface Props {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
  /** Appelé avant de réafficher les enfants — reset des queries en erreur (React Query). */
  readonly onReset?: () => void;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary captured:', error, info);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <Alert variant="destructive">
            <AlertTitle>Une erreur est survenue</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>
                La page n’a pas pu s’afficher correctement. Réessayez ; si le problème persiste,
                rechargez l’application (une session expirée peut en être la cause).
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  this.props.onReset?.();
                  this.setState({ error: null });
                }}
              >
                Réessayer
              </Button>
              <details className="text-xs">
                <summary className="cursor-pointer">Détail technique</summary>
                <pre className="mt-1 whitespace-pre-wrap">{this.state.error.message}</pre>
              </details>
            </AlertDescription>
          </Alert>
        )
      );
    }
    return this.props.children;
  }
}
